import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, CheckCircle2, CheckCircle, XCircle, Clock, Loader2, Info, ArrowLeft, AlertTriangle, ShieldOff, ArrowRight, CreditCard } from 'lucide-react';
import Modal from '../../common/Modal/Modal';
import { EmailData } from '../../../types/results.types';
import { EmailVerificationProvider, emailVerificationApi, VerificationStatusResponse, EmailVerificationEstimateResponse, VerifyPlaceEstimate } from '../../../api/emailVerification';
import Button from '../../common/Button/Button';
import ModalEmptyState from '../shared/ModalEmptyState';
import CompanySelector from '../shared/CompanySelector';
import Notification from '../../common/Notification/Notification';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { useBalance } from '../../../hooks/useBalance';
import { startEmailVerification, clearVerification, clearCompletedVerification } from '../../../store/slices/emailVerificationSlice';
import { fetchResults } from '../../../store/slices/resultsSlice';
import type { Scope } from '../ScopeToggle/ScopeToggle';
import { convertFiltersToApiFormat } from '../../../utils/filterConversion';
import { usePostHog } from 'posthog-js/react';

type Step = 'select' | 'confirm' | 'verifying' | 'results';

const VerificationCreditAmount: React.FC<{ amount: number; suffix?: string }> = ({ amount, suffix = 'credits' }) => (
  <span className="inline-flex items-center gap-1 whitespace-nowrap align-baseline">
    <Mail className="h-3.5 w-3.5 text-green-600" aria-hidden />
    <span>{amount.toLocaleString()}</span>
    {suffix && <span>{suffix}</span>}
  </span>
);

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  personEmails?: EmailData[];
  placeEmails?: EmailData[];
  emailType: 'person' | 'place' | 'both';
  searchId?: number;
  bulkMode?: boolean;
  defaultScope?: Scope;
  businessName?: string;
  onExportClick?: () => void;
  onUpgradeClick?: () => void;
  showCompletedOnOpen?: boolean;
  skipInitialSelection?: boolean;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  personEmails = [],
  placeEmails = [],
  emailType,
  searchId,
  bulkMode: _bulkMode,
  defaultScope,
  businessName,
  onExportClick,
  onUpgradeClick,
  showCompletedOnOpen = false,
  skipInitialSelection = false,
}) => {
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const { activeVerification, completedVerification, error: reduxError, loading: verificationRequestLoading } = useAppSelector((state) => state.emailVerification);
  const filters = useAppSelector((state) => state.results.filters);
  const pagination = useAppSelector((state) => state.results.pagination);
  const isVerificationInProgress = (verificationRequestLoading && !activeVerification) || !!(activeVerification && activeVerification.status === 'processing');
  const [step, setStep] = useState<Step>('select');

  const isDifferentVerificationActive = React.useMemo(() => {
    if (!activeVerification || activeVerification.status !== 'processing') return false;
    if (step === 'verifying') return false;
    return true;
  }, [activeVerification, step]);
  const [estimate, setEstimate] = useState<EmailVerificationEstimateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<VerificationStatusResponse | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<number>>(new Set());
  const [autoSelectedSubset, setAutoSelectedSubset] = useState(false);
  const [userModifiedSelection, setUserModifiedSelection] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  const [scope, setScope] = useState<Scope>('page');
  const prevIsOpenRef = useRef(false);
  const skipInitialSelectionConsumedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setScope(defaultScope ?? 'page');
      skipInitialSelectionConsumedRef.current = false;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, defaultScope]);

  const { balance, balanceLoading, balanceError, refreshBalance } = useBalance();
  const emailVerificationsRemaining = balance?.email_verifications_remaining ?? null;

  const personEmailIds = personEmails.filter((e) => !e.is_catch_all).map((email) => email.id || 0).filter((id) => id > 0);
  const placeEmailIds = placeEmails.filter((e) => !e.is_catch_all).map((email) => email.id || 0).filter((id) => id > 0);
  const allEmails = [...personEmails, ...placeEmails];
  const allEmailsCatchAll = allEmails.length > 0 && allEmails.every((e) => e.is_catch_all === true);
  const isBulkMode = scope === 'all';

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (showCompletedOnOpen && completedVerification && !syncResults && !isVerificationInProgress) {
      setStep('results');
      return;
    }
    if (isVerificationInProgress && step !== 'results') {
      setStep('verifying');
    } else if (completedVerification && !syncResults && step === 'verifying') {
      setStep('results');
    }
  }, [isOpen, activeVerification, completedVerification, syncResults, verificationRequestLoading, step, isVerificationInProgress, showCompletedOnOpen]);

  useEffect(() => {
    if (isOpen && step === 'select' && (isBulkMode || personEmailIds.length > 0 || placeEmailIds.length > 0) && !isVerificationInProgress) {
      loadEstimate();
    }
  }, [isOpen, step, personEmailIds.length, placeEmailIds.length, isBulkMode, filters, verificationRequestLoading, activeVerification]);

  useEffect(() => {
    if (isOpen && step === 'results') {
      refreshBalance();
    }
  }, [isOpen, step]);

  const loadEstimate = async () => {
    setLoading(true);
    setError(null);
    try {
      const estimateData = await emailVerificationApi.estimate(
        isBulkMode
          ? {
              bulk: true,
              search_id: searchId,
              email_type: emailType,
              ...convertFiltersToApiFormat(filters),
            }
          : {
              person_email_ids: personEmailIds,
              place_email_ids: placeEmailIds,
              email_type: emailType,
            }
      );
      setEstimate({ ...estimateData, places: estimateData.places ?? [] });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  // Auto-select companies to fit the credit budget when estimate first loads.
  // Server returns places sorted by ascending email_count so we cover the most companies.
  useEffect(() => {
    if (!estimate || emailVerificationsRemaining === null || userModifiedSelection) return;
    if (step === 'verifying' || step === 'results') return;
    const total = estimate.places.reduce((sum, p) => sum + p.email_count, 0);
    if (total > emailVerificationsRemaining) {
      let budget = emailVerificationsRemaining;
      const autoSelected = new Set<number>();
      for (const place of estimate.places) {
        if (budget >= place.email_count) {
          autoSelected.add(place.place_id);
          budget -= place.email_count;
        }
      }
      setSelectedPlaceIds(autoSelected);
      setAutoSelectedSubset(true);
    } else {
      setSelectedPlaceIds(new Set(estimate.places.map((p) => p.place_id)));
      setAutoSelectedSubset(false);
    }
  }, [estimate, emailVerificationsRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlace = (placeId: number) => {
    setUserModifiedSelection(true);
    setSelectedPlaceIds((prev) => {
      const next = new Set(prev);
      next.has(placeId) ? next.delete(placeId) : next.add(placeId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!estimate) return;
    setUserModifiedSelection(true);
    setSelectedPlaceIds(
      selectedPlaceIds.size === estimate.places.length
        ? new Set()
        : new Set(estimate.places.map((p) => p.place_id))
    );
  };

  const handleSelectMax = () => {
    if (!estimate || emailVerificationsRemaining === null) return;
    setUserModifiedSelection(true);
    let budget = emailVerificationsRemaining;
    const maxSelected = new Set<number>();
    for (const place of estimate.places) {
      if (budget >= place.email_count) {
        maxSelected.add(place.place_id);
        budget -= place.email_count;
      }
    }
    setSelectedPlaceIds(maxSelected);
  };

  const selectedEmailCount = estimate
    ? estimate.places
        .filter((p) => selectedPlaceIds.has(p.place_id))
        .reduce((sum, p) => sum + p.email_count, 0)
    : 0;

  useEffect(() => {
    if (!skipInitialSelection || skipInitialSelectionConsumedRef.current) return;
    if (!isOpen || step !== 'select' || isBulkMode || !estimate) return;
    if (balanceLoading || balanceError || emailVerificationsRemaining === null) return;
    if (isVerificationInProgress || isDifferentVerificationActive) return;
    if (emailVerificationsRemaining === 0 || selectedEmailCount === 0) return;
    if (selectedEmailCount > emailVerificationsRemaining) return;

    skipInitialSelectionConsumedRef.current = true;
    setStep('confirm');
  }, [
    skipInitialSelection,
    isOpen,
    step,
    isBulkMode,
    estimate,
    balanceLoading,
    balanceError,
    emailVerificationsRemaining,
    isVerificationInProgress,
    isDifferentVerificationActive,
    selectedEmailCount,
  ]);

  const effectivePersonEmailIds = estimate
    ? estimate.places
        .filter((p) => selectedPlaceIds.has(p.place_id))
        .flatMap((p) => p.person_email_ids)
    : [];

  const effectivePlaceEmailIds = estimate
    ? estimate.places
        .filter((p) => selectedPlaceIds.has(p.place_id))
        .flatMap((p) => p.place_email_ids)
    : [];

  const handleVerify = async () => {
    if (isDifferentVerificationActive) {
      setError('Another verification is in progress. Please complete or cancel it before starting a new one.');
      return;
    }

    if (selectedEmailCount === 0) {
      setError('Select at least one company to verify');
      return;
    }

    dispatch(clearCompletedVerification());
    setLoading(true);
    setError(null);

    posthog?.capture('verify_modal_confirmed', {
      email_count: selectedEmailCount,
      bulk_mode: isBulkMode,
    });

    try {
      const requestPayload = {
        person_email_ids: effectivePersonEmailIds,
        place_email_ids: effectivePlaceEmailIds,
        email_type: emailType,
        ...(searchId ? { search_id: searchId } : {}),
      };

      const response = await dispatch(
        startEmailVerification(requestPayload)
      ).unwrap();

      if ('processing_mode' in response && response.processing_mode === 'sync') {
        setSyncResults({
          status: response.status,
          results: response.results,
          credits_charged: response.credits_charged,
          credits_refunded: response.credits_refunded,
          summary: response.summary,
        });
        posthog?.capture('verify_completed', {
          credits_charged: response.credits_charged,
          credits_refunded: response.credits_refunded,
          valid_count: response.results?.filter((r: any) => r.status === 'GUARANTEED').length ?? 0,
          invalid_count: response.results?.filter((r: any) => r.status === 'INVALID').length ?? 0,
          sync: true,
        });
        setStep('results');
        if (searchId) {
          dispatch(fetchResults({
            searchId,
            page: pagination.page,
            limit: pagination.itemsPerPage,
            filters,
            silent: true,
          }));
        }
      } else {
        setStep('verifying');
      }
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.detail || (typeof err === 'string' ? err : 'Failed to start verification');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAndContinue = () => {
    onClose();
  };

  const resetModalFormState = () => {
    setStep('select');
    setEstimate(null);
    setSyncResults(null);
    setError(null);
    setScope('page');
    setShowCancelConfirm(false);
    setSelectedPlaceIds(new Set());
    setAutoSelectedSubset(false);
    setUserModifiedSelection(false);
    setIsStopping(false);
    skipInitialSelectionConsumedRef.current = false;
  };

  const handleClose = () => {
    if (activeVerification && activeVerification.status === 'processing') {
      onClose();
      return;
    }

    resetModalFormState();
    dispatch(clearCompletedVerification());
    onClose();
  };

  const handleFullClose = () => {
    resetModalFormState();
    dispatch(clearVerification());
    onClose();
  };

  if (!isOpen) return null;

  const getVerifiableCount = () => {
    if (!estimate) return 0;
    return estimate.places.reduce((sum, p) => sum + p.email_count, 0);
  };

  const renderAnotherVerificationBanner = () =>
    isDifferentVerificationActive ? (
      <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-3.5">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-base text-blue-800">Another verification is in progress. Please wait for it to complete.</p>
        </div>
      </div>
    ) : null;

  const renderEstimateSkeleton = () => (
    <div className="flex h-[560px] flex-col text-base overflow-hidden" aria-busy="true" aria-label="Loading verification estimate">
      {/* Scope tabs */}
      <div className="-mx-6 flex gap-0.5 border-b border-gray-200 px-6 mb-4">
        <div className="mb-[-1px] px-4 py-2">
          <div className="h-4 w-36 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="mb-[-1px] px-4 py-2">
          <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 space-y-3 overflow-hidden pb-2 pt-4">
        {/* Info banner */}
        <div className="h-9 w-full animate-pulse rounded-lg bg-blue-50" />

        {/* Credit stat cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-pulse rounded bg-gray-200" />
              <div className="space-y-1.5">
                <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-pulse rounded bg-gray-200" />
              <div className="space-y-1.5">
                <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-36 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          </div>
        </div>

        {/* Company selection header */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
          <div className="h-3.5 w-20 animate-pulse rounded bg-gray-100" />
        </div>

        {/* Select all row */}
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
        </div>

        {/* Company rows */}
        <div className="space-y-2 pr-1">
          {[0, 1, 2].map((item) => (
            <div key={item} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
                  <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
                  <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="h-5 w-12 animate-pulse rounded-full bg-gray-100" />
              </div>
              <div className="ml-16 mt-1.5 h-3.5 w-40 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>

      {/* Sticky footer */}
      <div className="flex gap-2 border-t border-gray-100 pt-4">
        <div className="h-10 flex-1 animate-pulse rounded-md bg-gray-100" />
        <div className="h-10 flex-[2] animate-pulse rounded-md bg-gray-200" />
      </div>
    </div>
  );

  const renderStepHeader = () => {
    const iconBoxClass = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#fdf5f5]';

    switch (step) {
      case 'select':
        return (
          <div className="flex items-center justify-between pb-4">
            <div className="flex items-center gap-2.5">
              <div className={iconBoxClass}>
                <Mail className="h-4 w-4 text-primary" strokeWidth={2} aria-hidden />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {businessName ? `Verify emails for ${businessName}` : 'Verify emails'}
              </h3>
            </div>
            <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-500 rounded-md p-0.5">
              <X className="w-5 h-5" />
            </button>
          </div>
        );
      case 'confirm':
        return (
          <div className="flex items-center justify-between pb-4">
            <div className="flex min-w-0 items-center gap-1">
              <button
                type="button"
                onClick={() => setStep('select')}
                className="text-gray-400 hover:text-gray-600 rounded-md p-0.5 shrink-0"
                aria-label="Back"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className={iconBoxClass}>
                <Mail className="h-4 w-4 text-primary" strokeWidth={2} aria-hidden />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 truncate">Confirm verification</h3>
            </div>
            <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-500 rounded-md p-0.5 shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        );
      case 'verifying':
        return (
          <div className="flex items-center pb-4">
            <div className={iconBoxClass}>
              <Mail className="h-4 w-4 text-primary" strokeWidth={2} aria-hidden />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 ml-2.5">Verify emails</h3>
          </div>
        );
      case 'results':
        return (
          <div className="flex items-center justify-between pb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-100">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2} aria-hidden />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Verification complete</h3>
            </div>
            <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-500 rounded-md p-0.5">
              <X className="w-5 h-5" />
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderCatchAllScreen = () => (
    <div className="flex flex-col items-center py-4 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-amber-200 bg-amber-50">
        <ShieldOff className="h-7 w-7 text-amber-500" />
      </div>
      <h4 className="mt-4 text-base font-semibold text-gray-900">Can't verify these emails</h4>
      <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-gray-500">
        This domain accepts any address, so verification can't confirm whether a specific inbox exists. No credits would be charged.
      </p>
      <p className="mt-3 text-xs text-gray-400">Export the emails as-is, or find personal contacts on other domains instead.</p>
      <div className="mt-5 flex flex-col gap-2 w-full">
        {onExportClick && (
          <Button
            onClick={() => { handleFullClose(); onExportClick(); }}
            variant="secondary"
            className="w-full justify-center"
          >
            Export as-is
          </Button>
        )}
        <Button onClick={handleClose} variant="secondary" className="w-full justify-center !border-gray-200 !text-gray-500">
          Close
        </Button>
      </div>
    </div>
  );

  const renderSelectScreen = () => {
    if (allEmailsCatchAll && !isBulkMode) {
      return renderCatchAllScreen();
    }

    if (loading && !estimate) {
      return renderEstimateSkeleton();
    }

    if (!estimate) {
      if (allEmails.length === 0 && !isBulkMode) {
        if (!businessName) {
          return (
            <ModalEmptyState
              heading="No emails to verify on this page"
              body="All emails on this page are already verified, or this page has none."
              ctaLabel="View all filtered results"
              onCtaClick={() => { setEstimate(null); setScope('all'); }}
              icon={<Mail className="h-6 w-6 text-gray-400" aria-hidden />}
            />
          );
        }
        return (
          <ModalEmptyState
            heading="No emails to verify"
            body="There are no emails available for verification."
            icon={<Mail className="h-6 w-6 text-gray-400" aria-hidden />}
            terminal
          />
        );
      }
      return null;
    }

    const verifiableTotal = getVerifiableCount();
    const remainingAfterSelection =
      emailVerificationsRemaining !== null ? emailVerificationsRemaining - selectedEmailCount : null;
    const overBudget = remainingAfterSelection !== null && remainingAfterSelection < 0;
    const noCredits = emailVerificationsRemaining === 0;
    const allSelected = estimate.places.length > 0 && selectedPlaceIds.size === estimate.places.length;

    const canProceed =
      !balanceLoading &&
      !balanceError &&
      !overBudget &&
      !noCredits &&
      selectedEmailCount > 0 &&
      !isDifferentVerificationActive;

    const ctaLabel = () => {
      if (balanceLoading) return 'Loading balance...';
      if (balanceError) return 'Balance unavailable — retry above';
      if (noCredits) return 'No verification credits';
      if (overBudget) {
        const over = selectedEmailCount - (emailVerificationsRemaining ?? 0);
        return `Deselect ${over} to continue`;
      }
      if (selectedEmailCount === 0) return 'Select companies to continue';
      return `Review & verify ${selectedEmailCount} email${selectedEmailCount !== 1 ? 's' : ''}`;
    };

    const scopeTabs = !businessName && (
      <div className="-mx-6 flex gap-0.5 border-b border-gray-200 px-6 mb-4">
        {(['all', 'page'] as const).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setEstimate(null);
              setScope(s);
              setSelectedPlaceIds(new Set());
              setUserModifiedSelection(false);
            }}
            className={`mb-[-1px] border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              scope === s
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            {s === 'all' ? 'All filtered results' : 'Current page'}
            {scope === s && estimate ? ` (${verifiableTotal})` : ''}
          </button>
        ))}
      </div>
    );

    if (verifiableTotal === 0) {
      const alreadyVerified =
        estimate.user_verified_emails.count > 0 &&
        estimate.generic_skipped.count === 0 &&
        (estimate.catch_all_skipped?.count ?? 0) === 0;
      return (
        <div className="flex flex-col gap-0">
          {scopeTabs}
          <ModalEmptyState
            heading={alreadyVerified ? 'Already verified' : 'Nothing to verify here'}
            body={
              alreadyVerified
                ? 'These emails are already checked. You can export now.'
                : 'Every email in this scope is already verified, catch-all, or flagged as generic — nothing left to check.'
            }
            icon={<CheckCircle2 className="h-6 w-6 text-gray-400" aria-hidden />}
            ctaLabel={onExportClick ? 'Export now' : undefined}
            onCtaClick={onExportClick ? () => { handleFullClose(); onExportClick!(); } : undefined}
            terminal={!onExportClick}
          />
          <Button onClick={handleClose} variant="secondary" className="w-full mt-2">
            Close
          </Button>
        </div>
      );
    }

    return (
      <div className="flex max-h-[560px] flex-col text-base" aria-live="polite">
        {scopeTabs}

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-2 pt-4">
          {/* Info banner */}
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
            <svg className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
            </svg>
            <p className="text-xs font-medium text-blue-700">
              Each check confirms the inbox exists and can receive mail.
            </p>
          </div>

          {/* Credit stat cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <div>
                  {balanceLoading ? (
                    <div className="h-4 w-6 animate-pulse rounded bg-gray-200" aria-label="Loading" />
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">
                      {selectedEmailCount.toLocaleString()} email{selectedEmailCount !== 1 ? 's' : ''} selected
                    </span>
                  )}
                  <p className="mt-0.5 text-xs text-gray-500">Based on your selection</p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                <div>
                  {balanceLoading ? (
                    <div className="h-4 w-10 animate-pulse rounded bg-gray-200" aria-label="Loading" />
                  ) : balanceError ? (
                    <button
                      type="button"
                      onClick={refreshBalance}
                      className="text-xs font-medium text-amber-700 underline hover:no-underline"
                    >
                      Retry balance
                    </button>
                  ) : (
                    <span className={`text-sm font-semibold ${overBudget ? 'text-red-600' : 'text-gray-900'}`}>
                      {emailVerificationsRemaining !== null ? `${emailVerificationsRemaining.toLocaleString()} credit${emailVerificationsRemaining !== 1 ? 's' : ''} available` : '—'}
                    </span>
                  )}
                  <p className="mt-0.5 text-xs text-gray-500">Only used when an email is verified</p>
                </div>
              </div>
            </div>
          </div>

          {/* Zero-credits banner — push upgrade hard */}
          {noCredits && !balanceError && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                <span className="text-xs font-medium text-amber-700">
                  You're out of verification credits — top up to keep your campaigns bounce-free.
                </span>
              </div>
              {onUpgradeClick && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50"
                >
                  Top up
                </button>
              )}
            </div>
          )}

          {/* Auto-selected info banner */}
          {/* {autoSelectedSubset && !userModifiedSelection && !noCredits && (
            <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
              <span className="text-xs font-medium text-blue-700">
                We've pre-selected the {selectedPlaceIds.size} companies that fit your {emailVerificationsRemaining} credits. Adjust below if needed.
              </span>
            </div>
          )} */}

          {/* Over-budget banner — only when user manually over-selected */}
          {overBudget && !noCredits && userModifiedSelection && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                <span className="text-xs font-medium text-amber-700">
                  {emailVerificationsRemaining !== null
                    ? `Over by ${selectedEmailCount - emailVerificationsRemaining} credit${selectedEmailCount - emailVerificationsRemaining === 1 ? '' : 's'}. Deselect companies, select the max, or top up.`
                    : 'Selection exceeds your credit balance.'}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {emailVerificationsRemaining !== null && emailVerificationsRemaining > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectMax}
                    className="flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50"
                  >
                    <ArrowRight className="h-3 w-3" aria-hidden />
                    Select max ({emailVerificationsRemaining})
                  </button>
                )}
                {onUpgradeClick && (
                  <button
                    type="button"
                    onClick={onUpgradeClick}
                    className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50"
                  >
                    Top up
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Skipped / already-verified notices */}
          {(estimate.generic_skipped.count > 0 || (estimate.catch_all_skipped?.count ?? 0) > 0 || estimate.user_verified_emails.count > 0) && (
            <div className="flex flex-col gap-1">
              {estimate.generic_skipped.count > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <XCircle className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span>{estimate.generic_skipped.count} generic/catch-all email{estimate.generic_skipped.count !== 1 ? 's' : ''} skipped — cannot verify individual inboxes</span>
                </div>
              )}
              {(estimate.catch_all_skipped?.count ?? 0) > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <XCircle className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span>{estimate.catch_all_skipped!.count} catch-all domain email{estimate.catch_all_skipped!.count !== 1 ? 's' : ''} skipped</span>
                </div>
              )}
              {estimate.user_verified_emails.count > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <CheckCircle className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span>{estimate.user_verified_emails.count} already verified — no credits needed</span>
                </div>
              )}
            </div>
          )}

          {/* Company selection header */}
          <div className="flex items-center justify-between gap-3">
            <h4 className="min-w-0 truncate text-sm font-semibold text-gray-900">Select companies to search</h4>
            {selectedPlaceIds.size > 0 && (
              <div className="flex shrink-0 items-center gap-2 text-sm">
                <span className="text-gray-500">{selectedPlaceIds.size} selected</span>
                <button
                  type="button"
                  style={{ paddingRight: '5px'}}
                  onClick={() => {
                    setUserModifiedSelection(true);
                    setSelectedPlaceIds(new Set());
                  }}
                  className="font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Select all toggle */}
          <div
            className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3.5 transition-colors hover:border-primary/60 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            onClick={toggleAll}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleAll();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <label className="flex cursor-pointer items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelected && selectedPlaceIds.size > 0;
                }}
                onChange={toggleAll}
                className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-base font-medium text-gray-800">Select all</span>
            </label>
            <span className="text-sm text-gray-600">
              {selectedPlaceIds.size} of {estimate.places.length} companies
            </span>
          </div>

          {/* Company rows */}
          <div className={`space-y-2 pr-1 transition-opacity ${loading ? 'pointer-events-none opacity-60' : ''}`} aria-busy={loading}>
            {estimate.places.map((place) => (
              <CompanySelector
                key={place.place_id}
                place={{ place_id: place.place_id, place_name: place.place_name, item_count: place.email_count }}
                selected={selectedPlaceIds.has(place.place_id)}
                onToggle={() => togglePlace(place.place_id)}
                icon="mail"
                itemNoun="email"
                unitLabel="verification"
                showAvatar
              />
            ))}
          </div>

          {renderAnotherVerificationBanner()}
        </div>

        {/* Sticky footer */}
        <div className="flex gap-2 border-t border-gray-100 pt-4">
          <Button onClick={handleClose} variant="secondary" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={() => setStep('confirm')}
            disabled={!canProceed}
            isLoading={loading && !estimate}
            variant={canProceed ? 'primary' : 'secondary'}
            className={`flex-[2] !text-base ${
              !canProceed
                ? '!cursor-not-allowed !border-gray-200 !bg-gray-200 !text-gray-400 hover:!bg-gray-200'
                : ''
            }`}
          >
            {ctaLabel()}
          </Button>
        </div>
      </div>
    );
  };

  const renderConfirmScreen = () => {
    if (!estimate) return null;

    const balanceAfter =
      emailVerificationsRemaining !== null
        ? Math.max(0, emailVerificationsRemaining - selectedEmailCount)
        : null;

    return (
      <div className="flex flex-col gap-0">
        <div className="flex flex-col gap-2 pt-1 text-sm leading-relaxed text-gray-700">
          <p>
            This will spend up to{' '}
            <strong>
              <VerificationCreditAmount amount={selectedEmailCount} />.
            </strong>{' '}
            Invalid addresses are refunded automatically, so your actual spend may be lower.
          </p>
          <p className="text-gray-600">
            SMTP check confirms whether the inbox accepts mail. Catch-all domains cannot be confirmed at the inbox level.
          </p>
        </div>

        <div className="mt-4 overflow-hidden rounded-[10px] border border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 text-sm">
            <span className="text-gray-600">Verifying</span>
            <span className="font-medium text-gray-900">
              {selectedEmailCount} email{selectedEmailCount !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 text-sm">
            <span className="text-gray-600">Credits</span>
            <span className="font-medium text-gray-900">
              up to <VerificationCreditAmount amount={selectedEmailCount} suffix="" />{' '}
              <span className="font-normal text-xs text-gray-400">· refunds applied after</span>
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-gray-600">Balance after</span>
            <span className="flex items-center gap-1 font-medium">
              {balanceAfter !== null ? (
                <>
                  <Mail className="h-3.5 w-3.5 text-green-600" aria-hidden />
                  <span className="text-green-600 font-semibold">{balanceAfter.toLocaleString()}+</span>
                  <span className="text-xs text-gray-500">verifies</span>
                </>
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </span>
          </div>
        </div>

        {renderAnotherVerificationBanner()}

        <div className="mt-4 flex gap-2">
          <Button onClick={() => setStep('select')} variant="secondary" className="flex-1">
            Back
          </Button>
          <Button
            onClick={handleVerify}
            isLoading={loading || isDifferentVerificationActive}
            variant="primary"
            className="flex-[2]"
            disabled={isDifferentVerificationActive}
          >
            Yes, verify {selectedEmailCount} email{selectedEmailCount !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    );
  };

  const renderVerifyingScreen = () => {
    const processed = activeVerification?.processed ?? 0;
    const total = activeVerification?.total ?? 0;
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
    const isDone = processed >= total && total > 0;

    const BATCH_CHUNK_SIZE = 50;
    const isReoonBulk = activeVerification?.verification_provider === EmailVerificationProvider.REOON && total >= 10;
    const isBatchRun = isReoonBulk || total > BATCH_CHUNK_SIZE;
    const currentChunk = !isReoonBulk && isBatchRun && processed > 0 ? Math.ceil(processed / BATCH_CHUNK_SIZE) : 0;
    const totalChunks = !isReoonBulk && isBatchRun ? Math.ceil(total / BATCH_CHUNK_SIZE) : 0;

    const byStatus = activeVerification?.by_status ?? {};
    const guaranteed = byStatus.GUARANTEED ?? 0;
    const risky = byStatus.RISKY ?? 0;
    const invalid = byStatus.INVALID ?? 0;
    const inboxFull = byStatus.INBOX_FULL ?? 0;
    const pending = Math.max(0, total - processed);

    const handleCancelClick = () => {
      if (isDone || isStopping) return;
      setShowCancelConfirm(true);
    };

    const handleCancelConfirm = async () => {
      setIsStopping(true);
      setShowCancelConfirm(false);
      if (activeVerification) {
        try {
          const { emailVerificationApi } = await import('../../../api/emailVerification');
          await emailVerificationApi.cancel(activeVerification.requestId);
        } catch {}
      }
      handleFullClose();
    };

    const handleRunInBackground = () => {
      handleCloseAndContinue();
    };

    if (showCancelConfirm) {
      return (
        <div className="pb-1 pt-2">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4">
            <p className="text-sm font-semibold text-amber-900 mb-1">Stop verification?</p>
            <p className="text-sm text-amber-800 mb-3">
              {processed > 0
                ? `${processed} already verified will be kept${isBatchRun ? ' and credits for the rest will be refunded.' : '.'}`
                : 'Credits for all unprocessed emails will be refunded.'}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => setShowCancelConfirm(false)} variant="secondary" className="flex-1">
                Keep going
              </Button>
              <button
                onClick={handleCancelConfirm}
                className="flex-1 rounded-lg bg-red-600 text-white text-sm font-semibold py-2 hover:bg-red-700 transition-colors"
              >
                Stop &amp; refund
              </button>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500">Or close this window — verification runs in the background.</p>
        </div>
      );
    }

    if (isStopping) {
      return (
        <div className="pb-1 pt-2 flex flex-col items-center gap-4 py-8">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          <p className="text-sm font-medium text-gray-700">Stopping verification…</p>
          <p className="text-xs text-gray-500 text-center px-4">
            Finishing the current batch and refunding remaining credits.
          </p>
        </div>
      );
    }

    return (
      <div className="pb-1 pt-2">
        <div className="flex items-center gap-3 mb-1">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
          <div>
            <span className="text-base font-medium text-gray-900">
              Verifying {total > 0 ? total : '…'} email{(total || 0) !== 1 ? 's' : ''}…
            </span>
            {isBatchRun && currentChunk > 0 && (
              <span className="ml-2 text-xs text-gray-400">
                batch {currentChunk} of {totalChunks}
              </span>
            )}
          </div>
        </div>

        {isBatchRun && (
          <p className="text-xs text-primary/80 mb-3 ml-8">
            Running in the background — safe to close anytime
          </p>
        )}

        {/* Progress bar */}
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 mb-1.5">
          <div
            className="h-full rounded-full bg-primary transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mb-4">
          <span>{processed} / {total || '…'}</span>
          <span>{pct}%</span>
        </div>

        {/* Rolling status counters */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 mb-4">
          {([
            { Icon: CheckCircle, label: 'valid',   n: guaranteed, iconClass: 'text-emerald-500', bgClass: 'bg-emerald-50', numClass: 'text-emerald-600' },
            { Icon: AlertTriangle, label: 'risky', n: risky,      iconClass: 'text-amber-500',   bgClass: 'bg-amber-50',   numClass: 'text-amber-600' },
            { Icon: AlertTriangle, label: 'inbox full', n: inboxFull, iconClass: 'text-amber-500', bgClass: 'bg-amber-50', numClass: 'text-amber-600' },
            { Icon: XCircle,      label: 'invalid', n: invalid,   iconClass: 'text-red-400',     bgClass: 'bg-red-50',     numClass: 'text-red-500' },
            { Icon: Clock,        label: 'pending', n: pending,   iconClass: 'text-gray-400',    bgClass: 'bg-gray-50',    numClass: 'text-gray-500' },
          ] as const).map(({ Icon, label, n, iconClass, bgClass, numClass }) => (
            <div key={label} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 ${bgClass}`}>
              <Icon className={`w-4 h-4 flex-shrink-0 ${iconClass}`} />
              <div>
                <div className={`text-lg font-extrabold leading-none ${numClass}`}>{n}</div>
                <div className="text-[10px] text-gray-400">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {isBatchRun ? (
          <div className="flex gap-2">
            <Button
              onClick={handleCancelClick}
              variant="secondary"
              className="flex-1"
              disabled={isDone}
            >
              {isDone ? 'Finishing…' : 'Stop'}
            </Button>
            <Button onClick={handleRunInBackground} variant="primary" className="flex-1">
              Run in background ↑
            </Button>
          </div>
        ) : (
          <>
            <p className="text-center text-sm text-gray-500 leading-snug px-1 mb-3">
              Safe to close — verification continues in background.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleCancelClick}
                variant="secondary"
                className="flex-1"
                disabled={isDone}
              >
                {isDone ? 'Finishing…' : 'Cancel'}
              </Button>
              <Button onClick={handleCloseAndContinue} variant="primary" className="flex-1">
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderResultsScreen = () => {
    const results = syncResults || completedVerification;
    if (!results) return null;

    const validCount = results.results.filter((r) => r.status === 'GUARANTEED').length;
    const invalidCount = results.results.filter((r) => r.status === 'INVALID').length;
    const riskyCount = results.results.filter(
      (r) => r.status === 'RISKY' || r.status === 'PENDING',
    ).length;
    const inboxFullCount = results.results.filter((r) => r.status === 'INBOX_FULL').length;

    const statBoxes = [
      { n: validCount, label: 'Verified', bg: 'bg-emerald-50', border: 'border-emerald-200', color: 'text-emerald-600' },
      { n: riskyCount, label: 'Risky', bg: 'bg-amber-50', border: 'border-amber-200', color: 'text-amber-600' },
      { n: inboxFullCount, label: 'Inbox full', bg: 'bg-amber-50', border: 'border-amber-200', color: 'text-amber-600' },
      { n: invalidCount, label: 'Invalid', bg: 'bg-red-50', border: 'border-red-200', color: 'text-red-500' },
    ];

    const steps = [
      { label: 'Find emails', done: true },
      { label: 'Verify emails', done: true },
      { label: 'Export', done: false, active: true },
    ];

    return (
      <div className="pb-1 pt-2 flex flex-col gap-4">
        {/* Deliverability breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {statBoxes.map(s => (
            <div
              key={s.label}
              className={`rounded-xl border ${s.border} ${s.bg} px-2 py-3 text-center`}
            >
              <div className={`text-2xl font-extrabold tracking-tight leading-none ${s.color}`}>{s.n}</div>
              <div className="text-[10.5px] text-gray-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Credit info */}
        <div className="overflow-hidden rounded-xl border border-gray-200 text-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
            <span className="text-gray-500">Credits spent</span>
            <span className="font-medium text-gray-900">{results.credits_charged}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-gray-500">Balance now</span>
            <span className="font-semibold text-emerald-600">
              {emailVerificationsRemaining !== null ? `${emailVerificationsRemaining.toLocaleString()} credits` : '—'}
            </span>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 flex-wrap rounded-xl bg-emerald-50 border border-emerald-200 px-3.5 py-2.5">
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && (
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="flex-shrink-0">
                  <path d="M0 5h10M6 1l4 4-4 4" stroke={s.done ? '#059669' : '#D1D5DB'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <div className="flex items-center gap-1.5">
                <div
                  className={`w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 ${
                    s.done
                      ? 'bg-emerald-500'
                      : s.active
                        ? 'bg-primary ring-2 ring-primary/20'
                        : 'bg-gray-200'
                  }`}
                >
                  {s.done ? (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l2.5 2.5L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <span className={`text-[9px] font-bold ${s.active ? 'text-white' : 'text-gray-400'}`}>{i + 1}</span>
                  )}
                </div>
                <span
                  className={`text-[11px] font-semibold ${
                    s.done ? 'text-emerald-600' : s.active ? 'text-primary' : 'text-gray-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <Button
          onClick={() => {
            handleFullClose();
            onExportClick?.();
          }}
          className="w-full"
        >
          Export list now
        </Button>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      appearance="product"
      headerSlot={renderStepHeader()}
    >
      {(error || reduxError) && (
        <Notification
          type="error"
          message={error || reduxError || 'An error occurred'}
          onClose={() => {
            setError(null);
          }}
        />
      )}

      {step === 'select' && !isVerificationInProgress && renderSelectScreen()}
      {step === 'confirm' && renderConfirmScreen()}
      {step === 'verifying' && renderVerifyingScreen()}
      {step === 'results' && renderResultsScreen()}
    </Modal>
  );
};

export default EmailVerificationModal;
